/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { domainAPI, useUpdateNameserversMutation } from 'api/domain'
import { setDNSMode } from 'store/slices/domainSlice'
import { DNSModeType } from 'models/enums'

/**********************************************************************************************************
 *   INTERFACES
 **********************************************************************************************************/
interface DNSModeFormValues {
    dns_config: string,
    nameservers: string[]
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function DnsModeForm() {
    const dispatch = useDispatch()
    const id = parseInt(location.pathname?.split('/')[2], 10)
    const dnsModeFormRef = useRef<FormikProps<DNSModeFormValues>>(null)
    const [updateNameservers] = useUpdateNameserversMutation()
    const { data: { dns_mode } = { dns_mode: '' } } = domainAPI.endpoints.dnsRecords.useQueryState(id)

    const AutoSubmitDNSMode = () => {
        const { initialValues, values, isSubmitting, submitForm } = useFormikContext<DNSModeFormValues>()

        useEffect(() => {
            if (values.dns_config !== DNSModeType.CustomNameservers && values.dns_config !== initialValues.dns_config && !isSubmitting) {
                submitForm()
            }

            if (values.dns_config) {
                dispatch(setDNSMode(values.dns_config))
            }
        }, [values, submitForm])

        return null
    }

    return (
        <Formik
            innerRef={dnsModeFormRef}
            enableReinitialize={true}
            initialValues={{
                dns_config: dns_mode,
                nameservers: []
            }}
            onSubmit={({ dns_config, nameservers }, { setSubmitting, resetForm }) => {
                if (dns_config === DNSModeType.CustomNameservers) {
                    updateNameservers({ id, dns_config, nameservers })
                } else {
                    updateNameservers({ id, dns_config })
                }
                setSubmitting(false)
                resetForm()
            }}
        >
            {() => (
                <DNSModeFormWrapper>
                    <Field
                        label={'Mode'}
                        name={'dns_config'}
                        type={'select'}
                        list={Object.values(DNSModeType).filter((value) => value !== 'Unknown').map((value: string) => ({
                            label: value,
                            value: value
                        }))}
                        component={NXUIForm.SelectField}
                    />
                    <AutoSubmitDNSMode/>
                </DNSModeFormWrapper>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const DNSModeFormWrapper = styled(Form)`
    margin: 0 20px;

    > div {
        max-width: 400px;
    }
`
